import { Component, Input, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { EmbedBuilderComponent } from '../embed-builder/embed-builder.component';
import { DiscordEmbed, EmbedField } from '../embed-builder/embed.model';
import { EmbedService } from 'src/app/services/embed.service';
import { BotGuild, CustomEmoji, CustomSignupTemplate, SignupGroupUsage, SignupTemplateEventTemplateUsage, SignupTemplateRepeatingEventUsage } from 'src/app/models/bot/guild';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { faBars, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Subscription } from 'rxjs';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GuildService } from 'src/app/services/guild.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SignupTemplateEventUsage } from 'src/app/models/bot/guild';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { EmojiComponentService } from 'src/app/services/emojiComponent.service';
import { ShareTemplateDialogComponent } from '../share-template-dialog/share-template-dialog.component';

interface generalSignupLimits {
  key: string;
  longName: string;
  value: number;
}

@Component({
  selector: 'app-signup-template-builder',
  templateUrl: './signup-template-builder.component.html',
  styleUrls: ['../../shared/styles/action-buttons.css', './signup-template-builder.component.css', '../embed-builder/embed-builder.component.css']
})
export class SignupTemplateBuilderComponent extends EmbedBuilderComponent implements OnInit {
  guild: BotGuild | undefined;
  emojiForm: FormGroup;
  initialFormValue: any;
  isFormDirty: boolean = false;
  isBreakingChange: boolean = false;
  @Input() signupTemplate: CustomSignupTemplate;
  @Input() serverCustomEmojis: CustomEmoji[];
  showEmojiPicker = false;
  signupConfig: DiscordEmbed = {}
  faIcons = {
    trash: faTrashAlt,
    bars: faBars,
  };
  private subscriptions = new Map<string, Subscription>();
  signupTemplateId: string;
  guildId: string;
  generalSignupLimits: generalSignupLimits[] = [
    {
      key: 'max_sign_ups_total',
      longName: 'Maximum sign ups to event',
      value: 0
    },
    {
      key: 'max_sign_ups_per_user',
      longName: 'Sign ups allowed per user',
      value: 1,
    },
    {
      key: 'max_sign_ups_per_role',
      longName: 'Maximum sign ups for all "role" emojis',
      value: 0
    },
    {
      key: 'max_sign_ups_per_status',
      longName: 'Maximum sign ups for all "status" emojis',
      value: 0
    }
  ]
  dialogEditTemplateRef: MatDialogRef<any>;
  dialogDeleteTemplateRef: MatDialogRef<any>;
  @ViewChild('editSignupTemplateDangerDialog') editSignupTemplateDangerDialog: TemplateRef<any>; // Reference to the dialog template
  @ViewChild('deleteSignupTemplateDangerDialog') deleteSignupTemplateDangerDialog: TemplateRef<any>; // Reference to the dialog template
  showConfirmButtons: boolean = false;
  resetSignal: boolean = false; // Reset signal for emoji picker
  isDesktop: boolean;

  constructor(
    embedService: EmbedService,
    private fb: FormBuilder,
    public guildService: GuildService,
    private botService: BotResourceService,
    private route: ActivatedRoute,
    private router: Router,
    public dialogTempalte: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private emojiComponentService: EmojiComponentService,
  ) {
    super(embedService);
  }

  ngOnInit(): void {
    this.guildId = this.route.parent.snapshot.paramMap.get('id');
    this.guildService.getGuildData(this.guildId).subscribe(guild => {
      this.guild = guild;
    });
    this.signupTemplateId = this.route.snapshot.paramMap.get('signupTemplateId');
    this.breakpointObserver.observe(["(max-width: 720px)"]).subscribe(result => {
      this.isDesktop = !result.matches;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.signupTemplateId = this.route.snapshot.paramMap.get('signupTemplateId');

    this.initForm();
    this.signupTemplateToEmbed();
    this.addSubscriptions(this.emojiForm);

    this.setupChangeDetection();

  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  private unsubscribeAll() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions.clear();
  }

  get emojis(): FormArray {
    return this.emojiForm.get('emojis') as FormArray;
  }

  initForm() {
    this.emojiForm = this.fb.group({
      emojis: this.fb.array([]),
      generalLimits: this.fb.group(
        this.generalSignupLimits.reduce((acc, limit) => ({
          ...acc,
          [limit.key]: [limit.value, [Validators.min(0), Validators.pattern("^[0-9]*$"), Validators.required]]
        }), {})
      ),
      signupTemplateTitle: [this.signupTemplate.name, [Validators.required, Validators.maxLength(25), Validators.minLength(1)]],
      signupTemplateIcon: [this.signupTemplate.icon, [Validators.required]],
      signupTemplateColor: [this.signupTemplate.color, [Validators.required]],
      signupTemplateEmojiBehavior: [this.signupTemplate.emoji_behavior, [Validators.required]],
      signupTemplateEmojiTitle: [this.signupTemplate.emoji_title ?? false],
      signupTemplateUseButtons: [this.signupTemplate.use_buttons ?? true],
    });
    // print the generalLimits
    this.initEmojis();
    this.initLimits();
    // TODO: Load this once on init and use this again when the route changes

    this.initialFormValue = JSON.parse(JSON.stringify(this.emojiForm.value)); // Deep copy initial state

    // Touch all form fields on load. This way we can show validation errors immediately
    this.emojiForm.markAllAsTouched();
  }

  initLimits() {
    // init general limits. Emoji field limits will be initialized in initEmojis
    Object.entries(this.signupTemplate.limits).forEach(([key, value]) => {
      const control = this.emojiForm.get(['generalLimits', key]);
      if (control) {
        control.setValue(value);
      }
    });
  }

  initEmojis() {
    const emojiControls = this.emojiForm.get('emojis') as FormArray;
    Object.keys(this.signupTemplate.emojis).forEach(key => {
      const value = this.signupTemplate.emojis[key];
      const subEmojiControls: FormArray = this.fb.array([]);

      if (value.emojis) {
        Object.keys(value.emojis).forEach(subKey => {
          let subLimit = this.signupTemplate.limits[value.type + "," + value.field_name + "," + value.emojis[subKey]?.field_name] || 0;
          subEmojiControls.push(this.fb.group({
            emoji: subKey,
            fieldName: [value.emojis[subKey]?.field_name, SignupTemplateBuilderComponent.fieldNameValidators(true)],
            limit: [subLimit, [Validators.min(0), Validators.pattern("^[0-9]*$"), Validators.required]]
          }));
        });
      }

      let mainLimit = this.signupTemplate.limits[value.type + "," + value.field_name] || 0;
      const emojiGroup = this.fb.group({
        emoji: key,
        fieldName: [value.field_name, SignupTemplateBuilderComponent.fieldNameValidators(false)],
        type: value.type,
        subEmojis: subEmojiControls,
        limit: [mainLimit, [Validators.min(0), Validators.pattern("^[0-9]*$"), Validators.required]]
      });
      this.attachFieldNameValidation(emojiGroup);
      emojiControls.push(emojiGroup);
    });
  }

  signupTemplateToEmbed() {
    // This function converts the signup template fields to a Discord embed
    // This way the embed preview component can display the changes
    this.signupConfig.title = "Example Event Preview";
    this.signupConfig.description = '**Event Info:**  \n🗓️ < event date >  \n🕑 < event start > - < event end >'
    this.signupConfig.color = this.signupTemplate.color;
    this.signupTemplateEmojisToEmbedFields();
  }

  parseCustomEmojiForEmbed(emoji: string) {
    // This function returns an image tag with the emoji to be displayed in the embed preview
    const customEmoji = this.serverCustomEmojis.find(customEmoji => emoji.includes(customEmoji.id));
    if (customEmoji) {
      return `<img class="custom-button-image" src="${customEmoji.imageUrl}" alt="${customEmoji.name}"></img>`
    } else {
      // Emoji does not belong to this server. We try best effort to display the emoji anyway.
      // Emoji comes in format of `<a:name:id>` if anmiated, else `<name:id>`
      // We need to get the id and append it to the url `https://cdn.discordapp.com/emojis/<id>`
      // We also need to check if the emoji is animated. If it is, we append `.gif` to the url, else `.png`
      const emojiParts = emoji.match(/<a?:(\w+):(\d+)>/);
      if (emojiParts && emojiParts.length === 3) {
        const isAnimated = emoji.startsWith('<a:');
        const emojiId = emojiParts[2];
        const imageUrl = `https://cdn.discordapp.com/emojis/${emojiId}${isAnimated ? '.gif' : '.png'}`;
        return `<img class="custom-button-image" src="${imageUrl}" alt="${emojiParts[1]}"></img>`;
      }
    }
  }

  parseEmojiForReaction(emoji: string, fieldName: string, type?: string) {
    // This function returns the DiscordEmoji instance for the reaction
    if (type === 'remove') {
      fieldName = 'Sign Off';
    } else if (type === 'late') {
      fieldName = 'Late (+5 min)';
    } else if (type === 'rlate') {
      fieldName = 'Remove Late';
    } else {
      fieldName = fieldName.replace(", ", " - ");
    }

    const customEmoji = this.serverCustomEmojis.find(customEmoji => emoji.includes(customEmoji.id));
    if (customEmoji) {
      return {url: customEmoji.imageUrl, name: customEmoji.name, count: 1, fieldName: fieldName, type: type};
    } else if (emoji.includes(':') && emoji.includes('<') && emoji.includes('>')) {
      const emojiParts = emoji.match(/<a?:(\w+):(\d+)>/);
      if (emojiParts && emojiParts.length === 3) {
        const isAnimated = emoji.startsWith('<a:');
        const emojiId = emojiParts[2];
        const imageUrl = `https://cdn.discordapp.com/emojis/${emojiId}${isAnimated ? '.gif' : '.png'}`;
        return {url: imageUrl, name: emojiParts[1], count: 1, fieldName: fieldName, type: type};
      }
    }
    return {url: emoji, name: emoji, count: 1, fieldName: fieldName, type: type};
  }

  isCustomEmoji(emoji: string) {
    return emoji.includes(':') && emoji.includes('<') && emoji.includes('>')
  }

  private updateFields() {
    // This function calls everything that needs to be updated on a change
    this.signupTemplateEmojisToEmbedFields();
    this.validateEmojis()

    // this.signupTemplateEmojisToLimits();
  }

  private signupTemplateEmojisToLimits() {
    // This function updates the limits of each emoji
    (this.emojiForm.get('emojis') as FormArray).controls.forEach(control => {
      control.get('limit').setValue(control.get('subEmojis').value.length);
    });
  }

  private signupTemplateEmojisToEmbedFields(): void {
    const roleFields: EmbedField[] = [];
    this.emojiComponentService.signupTemplateEmojisToEmbedFields(
      this.embedService,
      this.serverCustomEmojis,
      { currEmbed: this.signupConfig },
      {
        emojis: (this.emojiForm.get('emojis') as FormArray).controls.map(control => control.value),
        emojiBehavior: this.emojiForm.get('signupTemplateEmojiBehavior').value,
        useButtons: this.emojiForm.get('signupTemplateUseButtons').value,
        color: this.emojiForm.get('signupTemplateColor').value,
        emojiTitle: this.emojiForm.get('signupTemplateEmojiTitle').value
      }
    );
  }

  addEmoji() {
    const emojiControls = this.emojiForm.get('emojis') as FormArray;
    const emojiGroup = this.fb.group({
      type: ['', Validators.required],
      fieldName: ['', SignupTemplateBuilderComponent.fieldNameValidators(false)],
      emoji: ['', Validators.required],
      limit: [0, [Validators.min(0), Validators.pattern("^[0-9]*$"), Validators.required]],
      subEmojis: this.fb.array([])
    });
    emojiControls.push(emojiGroup);
    this.attachFieldNameValidation(emojiGroup);

    this.updateFields();
  }

  /** Names are stored as `type,name` segments, so a comma would be parsed as another segment. */
  private static noCommaValidator(control: AbstractControl): ValidationErrors | null {
    if (typeof control.value === 'string' && control.value.includes(',')) {
      return { comma: true };
    }
    return null;
  }

  private static fieldNameValidators(required: boolean): ValidatorFn[] {
    const validators: ValidatorFn[] = [SignupTemplateBuilderComponent.noCommaValidator];
    if (required) {
      validators.unshift(Validators.required);
    }
    return validators;
  }

  private attachFieldNameValidation(emojiGroup: FormGroup) {
    const typeControl = emojiGroup.get('type');
    this.updateFieldNameValidator(emojiGroup, typeControl?.value ?? '');
    typeControl?.valueChanges.subscribe(value => {
      this.updateFieldNameValidator(emojiGroup, value);
    });
  }

  updateFieldNameValidator(emojiGroup: FormGroup, type: string) {
    const fieldNameControl = emojiGroup.get('fieldName');
    if (!fieldNameControl) {
      return;
    }
    const requiresName = type === 'role' || type === 'status';
    fieldNameControl.setValidators(SignupTemplateBuilderComponent.fieldNameValidators(requiresName));
    fieldNameControl.updateValueAndValidity();
  }

  addSubEmoji(parentIndex: number) {
    const subEmojis = this.emojis.at(parentIndex).get('subEmojis') as FormArray;
    const subEmojiFormGroup = this.fb.group({
      fieldName: ['', SignupTemplateBuilderComponent.fieldNameValidators(true)],
      emoji: ['', Validators.required],
      limit: [0, [Validators.min(0), Validators.pattern("^[0-9]*$"), Validators.required]]
    });
    subEmojis.push(subEmojiFormGroup);
    this.updateFields();
  }

  removeEmoji(index: number) {
    this.disposeSubscription(`.emojis.${index}`);
    const emojiControls = this.emojiForm.get('emojis') as FormArray;
    emojiControls.removeAt(index);
    // this.reinitializeSubscriptions(emojiControls, '.emojis');
    this.updateFields();
  }

  removeSubEmoji(parentIndex: number, index: number) {
    const subEmojis = this.emojis.at(parentIndex).get('subEmojis') as FormArray;
    subEmojis.removeAt(index)
    this.updateFields();
  }

  toggleEmojiPicker() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  closeEmojiPicker() {
    // Use setTimeout to delay the closure to allow for emoji selection
    setTimeout(() => this.showEmojiPicker = false, 200);
  }

  extractEmojiId(emoji: string): string {
    // emoji can be both just `<a:3042loading:839545303287595058>` or the whole url
    // <img class="custom-button-image" src="https://cdn.discordapp.com/emojis/839545303287595058.gif" alt="<a:3042loading:839545303287595058>">
    // It can also be saved as either in the form.
    // The machting should handle that.
    if (!emoji) {
      return '';
    }

    const shortEmojiMatch = emoji.match(/<a?:\w+:(\d+)>/);
    if (shortEmojiMatch) {
      return shortEmojiMatch[1];
    }

    const urlMatch = emoji.match(/src="https:\/\/cdn\.discordapp\.com\/emojis\/(\d+)\./);
    if (urlMatch) {
      return urlMatch[1];
    }

    return emoji; // for standard Unicode emojis, return the emoji itself
  }

  isSubEmojiDuplicate(groupIndex: number, emoji: string): boolean {
    const subEmojisArray = this.emojis.at(groupIndex).get('subEmojis') as FormArray;
    const subEmojis = subEmojisArray.controls.map(control => control.get('emoji').value);
    const emojiId = this.extractEmojiId(emoji);
    return subEmojis.some(e => this.extractEmojiId(e) === emojiId);
  }

  isEmojiDuplicate(emoji: string): boolean {
    const emojis = this.emojis.controls.map(control => control.get('emoji').value);
    const emojiId = this.extractEmojiId(emoji);
    return emojis.some(e => this.extractEmojiId(e) === emojiId);
  }

  getTopEmojis(): string[] {
    return this.emojis.controls.map(control => control.get('emoji').value).filter(value => value);
  }

  getSubEmojis(): string[] {
    return this.emojis.controls.reduce((acc, control) => {
      const subEmojisArray = control.get('subEmojis') as FormArray;
      return acc.concat(subEmojisArray.controls.map(subControl => subControl.get('emoji').value));
    }, []).filter(value => value);
  }

  isEmojiUsed(emoji: string): number {
    const topEmojis = this.getTopEmojis();
    const subEmojis = this.getSubEmojis();
    const emojiId = this.extractEmojiId(emoji);
    const topEmojiCount = topEmojis.filter(e => this.extractEmojiId(e) === emojiId).length;
    const subEmojiCount = subEmojis.filter(e => this.extractEmojiId(e) === emojiId).length;
    return topEmojiCount + subEmojiCount;
  }

  handleEmojiSelection(control: any, emoji: string, groupIndex: number, isSubEmoji: boolean): void {
    const emojiBehavior = this.emojiForm.get('signupTemplateEmojiBehavior').value;
    if (emojiBehavior === "2" || emojiBehavior === "3") {
      if (isSubEmoji && this.isSubEmojiDuplicate(groupIndex, emoji)) {
        alert('This emoji is already used in this sub-emoji group.');
        this.triggerResetSignal();
      } else if (!isSubEmoji && this.isEmojiDuplicate(emoji)) {
        alert('This emoji is already used as a main emoji.');
        this.triggerResetSignal();
      } else {
        control.setValue(emoji);
        this.signupTemplateToEmbed();
      }
    } else if (emojiBehavior === "1") {
      if (this.isEmojiUsed(emoji)) {
        alert('This emoji is already used.');
        this.triggerResetSignal();
      } else {
        control.setValue(emoji);
        this.signupTemplateToEmbed();
      }
    }
  }

  triggerResetSignal() {
    this.resetSignal = true;
    setTimeout(() => {
      this.resetSignal = false;
    }, 0);
  }

  private addSubscriptions(formGroup: FormGroup | FormArray, path = '') {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      const controlPath = `${path}.${key}`;

      // If it's a FormGroup or FormArray, recursively subscribe to its children
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.addSubscriptions(control, controlPath);
      }

      if (control instanceof FormControl) {
        if (!this.subscriptions.has(controlPath)) {
          const sub = control.valueChanges.subscribe(value => {
            this.updateFields();
            // Call your update function if necessary
          });
          this.subscriptions.set(controlPath, sub);
        }
      }
    });

    // Listen for changes in the structure of FormArray
    if (formGroup instanceof FormArray) {
      const sub = formGroup.valueChanges.subscribe(() => this.reinitializeSubscriptions(formGroup, path));
      this.subscriptions.set(path, sub);
    }
  }

  private reinitializeSubscriptions(formArray: FormArray, path: string) {
    // Clear existing subscriptions for this path
    formArray.controls.forEach((_, index) => {
      const controlPath = `${path}.${index}`;
      this.disposeSubscription(controlPath);
      this.addSubscriptions(formArray.at(index) as FormGroup, controlPath);
    });
  }

  private disposeSubscription(path: string) {
    this.subscriptions.forEach((sub, key) => {
      if (key.startsWith(path)) {
        sub.unsubscribe();
        this.subscriptions.delete(key);
      }
    });
  }

  setupChangeDetection() {
    this.emojiForm.valueChanges.subscribe(() => {
      const form_valid = this.checkFormValidity();
      this.isFormDirty = form_valid && JSON.stringify(this.emojiForm.value) !== JSON.stringify(this.initialFormValue);
      this.isBreakingChange = form_valid && this.form_breaking_field_changed();
    });
  }

  checkFormValidity(): boolean {
    const requiredFieldsValid = this.emojiForm.get('signupTemplateTitle')!.valid &&
                                this.emojiForm.get('signupTemplateIcon')!.valid &&
                                this.emojiForm.get('signupTemplateColor')!.valid &&
                                this.emojiForm.get('signupTemplateEmojiBehavior')!.valid;
    const atLeastOneValidEmoji = this.emojis.length > 0 && this.emojis.controls.every(ctrl => {
      const emoji = ctrl.get('emoji')?.value;
      return !!emoji && ctrl.valid; // Ensure emoji field is not empty and control is valid
    });
    return requiredFieldsValid && atLeastOneValidEmoji;
  }

  private stripLimitsFromEmojis(emojis: any[]): any[] {
    return emojis.map(emoji => ({
      ...emoji,
      limit: undefined, // Remove limit from comparison
      subEmojis: emoji.subEmojis.map((subEmoji: any) => ({
        ...subEmoji,
        limit: undefined // Remove limit from sub-emojis as well
      }))
    }));
  }
  form_breaking_field_changed(): boolean {
    // Returns true if either `emojis` or `emoji_behavior` changed
    const currentEmojis = this.stripLimitsFromEmojis(this.emojiForm.value.emojis);
    const initialEmojis = this.stripLimitsFromEmojis(this.initialFormValue.emojis);

    return JSON.stringify(currentEmojis) !== JSON.stringify(initialEmojis) ||
           this.emojiForm.get('signupTemplateEmojiBehavior')!.value !== this.initialFormValue.signupTemplateEmojiBehavior;
  }

  saveTemplate() {
    this.isFormDirty = false
    if (this.signupTemplateId === null) {
      this.botService.createSignupTemplate(this.guildId, this.emojiForm.value).subscribe({
        next: (response: any) => {
          if (response.status === 201) {
            const templateId = response.body.id;
            const newTemplatePath = `/guilds/${this.guildId}/signup-template/${templateId}`;
            this.guild.signup_templates.push({
              id: templateId,
              name: response.body.name
            });
            this.router.navigate([newTemplatePath]); // Navigate to the new URL
          }
        },
        error: (error: any) => {
          console.error('Error creating template:', error);
          this.isFormDirty = true
          // Add error handling logic here, like showing an error message
        }
      });
    } else {
      if (this.isBreakingChange) {
        // open danger dialog, but with confirm and cancel buttons
        this.showConfirmButtons = true;
        this.openEditSignupTempalteDangerDialog();
      } else {
        this.updateTemplate(false);
      }
    }
  }

  deleteTemplate() {
    this.openDeleteSignupTempalteDangerDialog();
  }

  shareTemplate() {
    if (!this.signupTemplateId || this.isFormDirty) {
      return;
    }
    this.dialogTempalte.open(ShareTemplateDialogComponent, {
      panelClass: 'raid-organizer-dialog',
      width: '480px',
      data: {
        guildId: this.guildId,
        templateId: this.signupTemplateId,
        defaultTitle: this.signupTemplate?.name || this.emojiForm?.get('signupTemplateTitle')?.value || '',
      },
    });
  }

  openDeleteSignupTempalteDangerDialog() {
    this.dialogDeleteTemplateRef = this.dialogTempalte.open(this.deleteSignupTemplateDangerDialog, {
      panelClass: 'raid-organizer-dialog',
    });

    this.dialogDeleteTemplateRef.afterClosed().subscribe(result => {
      if (result) {
        // Handle the action if "Continue" is clicked in the dialog
        this.deleteTemplateConfirmed();
    } else {
        // Handle the action if "Cancel" is clicked in the dialog
      }
    });
  }

  deleteTemplateConfirmed() {
    this.botService.deleteSignupTemplate(this.guildId, this.signupTemplateId).subscribe({
      next: () => {
        this.guild.signup_templates = this.guild.signup_templates.filter(template => template.id !== this.signupTemplateId);
        this.router.navigate([`/guilds/${this.guildId}/signup-template/new`]);
      },
      error: (error: any) => {
        console.error('Error deleting template:', error);
        // Add error handling logic here, like showing an error message
      }
    });
  }

  confirmDeleteAction() {
    this.dialogDeleteTemplateRef.close(true);
  }

  updateTemplate(idChange: boolean) {
    this.botService.postSignupTemplate(this.guildId, this.signupTemplateId, this.emojiForm.value, idChange).subscribe({
      next: (result) => {
        this.initialFormValue = JSON.parse(JSON.stringify(this.emojiForm.value)); // Deep copy initial state
        // find signup template with id in list and update it's name
        this.guild.signup_templates = this.guild.signup_templates.map(template => {
          if (template.id === this.signupTemplateId) {
            template.name = this.emojiForm.get('signupTemplateTitle').value;
          }
          return template;
        });
        this.signupTemplate = result
        this.isFormDirty = false
      },
      error: (err) => {
        this.isFormDirty = true
      }
    });
  }

  openEditSignupTempalteDangerDialog(): void {
    this.dialogEditTemplateRef = this.dialogTempalte.open(this.editSignupTemplateDangerDialog, {
      panelClass: 'raid-organizer-dialog',
    });

    this.dialogEditTemplateRef.afterClosed().subscribe(result => {
      if (result) {
        // Handle the action if "Continue" is clicked in the dialog
        this.updateTemplate(true);
        this.confirmEditAction();
      } else {
        // Handle the action if "Cancel" is clicked in the dialog
        if (this.showConfirmButtons) {
          this.showConfirmButtons = false;
          this.isFormDirty = true;
        }
      }
    });
  }

  confirmEditAction(): void {
    this.dialogEditTemplateRef.close(true);
  }


  get hasTemplateUsages(): boolean {
    return this.getEventEntries().length > 0
      || this.getRepeatingEventEntries().length > 0
      || this.getEventTemplateEntries().length > 0
      || this.getSignupGroupEntries().length > 0;
  }

  getEventEntries(): SignupTemplateEventUsage[] {
    return this.signupTemplate.used_by?.events
      ? Object.values(this.signupTemplate.used_by.events)
      : [];
  }

  getRepeatingEventEntries(): SignupTemplateRepeatingEventUsage[] {
    return this.signupTemplate.used_by?.repeating_events
      ? Object.values(this.signupTemplate.used_by.repeating_events)
      : [];
  }

  getEventTemplateEntries(): SignupTemplateEventTemplateUsage[] {
    return this.signupTemplate.used_by?.event_templates
      ? Object.values(this.signupTemplate.used_by.event_templates)
      : [];
  }

  getSignupGroupEntries(): SignupGroupUsage[] {
    return this.signupTemplate.used_by?.signup_groups
      ? Object.values(this.signupTemplate.used_by.signup_groups)
      : [];
  }

  isTypeSelected(type: string): boolean {
    return this.emojis.controls.some(control => control.get('type')?.value === type);
  }

  validateEmojis() {
    const emojiBehavior = this.emojiForm.get('signupTemplateEmojiBehavior').value;
    let emojiCount = 0;

    this.emojis.controls.forEach((group, groupIndex) => {
      const subEmojisArray = group.get('subEmojis') as FormArray;
      const topEmoji = group.get('emoji').value;
      emojiCount += 1;

      // Validate top-level emoji
      if (emojiBehavior === "1" && this.isEmojiUsed(topEmoji) > 1) {
        group.get('emoji').setErrors({ duplicate: true });
      } else if (emojiBehavior === "1" && emojiCount > 20) {
        group.get('emoji').setErrors({ emojiLimit: true });
      } else {
        group.get('emoji').setErrors(null);
      }

      // Validate sub-emojis
      let subEmojiCount = 0;
      subEmojisArray.controls.forEach((control, subIndex) => {
        if (emojiBehavior === "1") {
          emojiCount += 1;
        } else {
          subEmojiCount += 1;
        }
        const subEmoji = control.get('emoji').value;
        if (!subEmoji) {
          control.get('emoji').setErrors({ required: true });
        } else if (emojiBehavior === "1" && this.isEmojiUsed(subEmoji) > 1) {
          control.get('emoji').setErrors({ duplicate: true });
        } else if (emojiBehavior === "1" && emojiCount > 20) {
          control.get('emoji').setErrors({ emojiLimit: true });
        } else if (emojiBehavior !== "1" && subEmojiCount > 20) {
          control.get('emoji').setErrors({ subEmojiLimit: true });
        } else {
          control.get('emoji').setErrors(null);
        }
      });
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.emojis.controls, event.previousIndex, event.currentIndex);
    this.emojis.updateValueAndValidity();
    this.updateFields()
  }

  dropSubEmoji(event: CdkDragDrop<string[]>, groupIndex: number) {
    const subEmojis = this.emojis.at(groupIndex).get('subEmojis') as FormArray;
    moveItemInArray(subEmojis.controls, event.previousIndex, event.currentIndex);
    subEmojis.updateValueAndValidity();
    this.updateFields()
  }
}
